import React from 'react'
import './index.css'
import Flex from 'components/Flex'
import logUtil from 'utils/log'
import useSelector from 'hooks/use-selector'
import { SQUAD_FILTER_TYPE } from 'modules/squad/redux/constants'
import loadMemberProductsByFilterTypeSquadAction from 'modules/squad/redux/actions/load-member-products-by-filter-type'
import useDispatch from 'hooks/use-dispatch'
import getMemberProductsByIdAndFilterTypeSquadSelector from 'modules/squad/redux/selectors/get-member-products-by-id-and-filter-type'
import FlexColumn from 'components/FlexColumn'
import pluralizeStringUtil from 'utils/string/plurialize'
import logEventAnalyticsUtil from 'utils/analytics/log-event'
import Button from 'components/Button'
import noopFunctionUtil from 'utils/functions/noop'
import Section from 'components/Section'
import areProductsLoadedByMemberIdSquadSelector from 'modules/squad/redux/selectors/are-products-loaded-by-member-id'
import H3 from 'components/H3'
import { SQUAD_LIKED_PRODUCTS_ID } from 'modules/squad/constants'
import usePrevious from 'hooks/use-previous'
import ProductCollectionItem from 'containers/ProductCollection/components/Item'
import isArchivedProductVariantUtil from 'modules/product-variant/utils/is-archived/index.js'

const ANALYTICS_EVENT = {
  VIEW_SQUAD_FRIEND_LIKED_PRODUCTS: 'VIEW_SQUAD_FRIEND_LIKED_PRODUCTS',
  SEE_MORE_SQUAD_FRIEND_LIKED_PRODUCTS: 'SEE_MORE_SQUAD_FRIEND_LIKED_PRODUCTS',
  VIEW_LIKED_PRODUCTS: 'VIEW_LIKED_PRODUCTS',
  SHOW_SQUAD_LIKED_PRODUCTS: 'SHOW_SQUAD_LIKED_PRODUCTS',
  SEE_MORE_SQUAD_LIKED_PRODUCTS: 'SEE_MORE_SQUAD_LIKED_PRODUCTS'
}
const PAGE_SIZE = 8
const SKIP_COUNT = 0
const SEE_MORE = 'See more'
const LOADING = 'Loading...'

export default function SquadPageLikedSamples (props) {
  const dispatch = useDispatch()
  const {
    id,
    name,
    isCurrentCustomer
  } = props
  const isLoaded = useSelector(areProductsLoadedByMemberIdSquadSelector(id, SQUAD_FILTER_TYPE.like))
  /**
   * @type {import('modules/squad/types').GetSquadProductsResp}
   */
  const likedSamplesData = useSelector(getMemberProductsByIdAndFilterTypeSquadSelector(id, SQUAD_FILTER_TYPE.like))
  const products = likedSamplesData?.products
  const productsCount = products?.length
  const totalProductsCount = likedSamplesData?.total_product_count
  const hasMoreProducts = productsCount < totalProductsCount
  const debugProps = {
    isLoaded,
    likedSamplesData,
    totalProductsCount,
    hasMoreProducts,
    props
  }

  const prevProductsCount = usePrevious(productsCount)
  React.useEffect(() => {
    if (!prevProductsCount && productsCount) {
      logEventAnalyticsUtil(ANALYTICS_EVENT.SHOW_SQUAD_LIKED_PRODUCTS)
    }
  }, [
    prevProductsCount,
    productsCount
  ])

  const loadProducts = (params) => {
    dispatch(
      loadMemberProductsByFilterTypeSquadAction({
        memberId: id,
        filterType: SQUAD_FILTER_TYPE.like,
        pageSize: PAGE_SIZE,
        skipCount: SKIP_COUNT,
        ...params
      })
    )
  }

  React.useEffect(() => {
    if (id && !isLoaded) {
      loadProducts()
    }
  }, [id])

  const handleClickSeeMore = () => {
    logEventAnalyticsUtil(
      isCurrentCustomer
        ? ANALYTICS_EVENT.SEE_MORE_SQUAD_LIKED_PRODUCTS
        : ANALYTICS_EVENT.SEE_MORE_SQUAD_FRIEND_LIKED_PRODUCTS
    )
    loadProducts({
      skipCount: productsCount
    })
  }

  logUtil('SquadPageLikedSamples').debug('debugProps=', debugProps)
  if (!isLoaded && !productsCount) {
    logUtil('SquadPageLikedSamples').debug('[noop] not loaded')
    return <></>
  }

  if (!productsCount) {
    logUtil('SquadPageLikedSamples').debug('[noop] no likedSamplesData')
    return <></>
  }

  return (
    <Section
      className='squad-liked-samples'
      id={SQUAD_LIKED_PRODUCTS_ID}
    >
      <FlexColumn
        className='squad-liked-samples-content'
      >
        <Flex className='squad-liked-samples-title'>
          <svg
            width='32'
            height='32'
            viewBox='0 0 32 32'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M10.6594 7.00421C10.0037 7.00421 9.30686 7.13797 8.57829 7.44487C5.39492 8.78497 4.58874 12.1586 5.18684 14.7263C6.65353 21.0261 14.6624 24.6236 15.6711 25.0535C16.6797 24.623 24.6889 21.0216 26.155 14.7263C26.7531 12.1586 25.947 8.78468 22.7633 7.44458C19.6606 6.13821 17.1316 7.966 15.909 9.92784C15.8066 10.093 15.5361 10.093 15.4329 9.92784C14.4971 8.42678 12.7965 7.00421 10.6594 7.00421Z'
              fill='#E15EC4'
            />
          </svg>
          <H3>{isCurrentCustomer ? 'You’ve' : `${name} has`} liked {totalProductsCount} {pluralizeStringUtil('sample', totalProductsCount)}</H3>
        </Flex>
        <Flex className='squad-liked-samples-list'>
          {
            likedSamplesData.products.map((sampleProduct, index) => {
              const key = `${sampleProduct.product_id}-${sampleProduct.variant_id}-${index}`
              const isArchived = isArchivedProductVariantUtil(sampleProduct)
              return (

                <div
                  key={key}
                  className='product-collection-item-wrapper product'
                >
                  <ProductCollectionItem
                    productVariant={sampleProduct}
                    isArchived={isArchived}
                  />
                </div>
              )
            })
          }
        </Flex>
        {!!hasMoreProducts && (
          <Button
            className='squad-liked-samples-see-more'
            onClick={isLoaded ? handleClickSeeMore : noopFunctionUtil}
          >
            {isLoaded ? SEE_MORE : LOADING}
          </Button>
        )}
      </FlexColumn>
    </Section>
  )
}
